import { createServerFn } from "@tanstack/react-start";

import {
  catalogImagePresignInputSchema,
  type ValidatedCatalogImagePresignInput,
} from "@/api/contracts/catalog-images";
import { requireAuth } from "@/api/middleware/auth";
import {
  uploadToStorage,
  type PresignUploadFn,
  type PresignedUpload,
  type UploadProgressFn,
} from "@/lib/upload-to-storage";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// The browser's door onto the catalog image pipeline's upload half: ask for a
// presigned PUT, then upload the bytes straight to storage
// (`@/lib/upload-to-storage`). The bytes never pass through the BFF.
//
// **`requireAuth` and nothing more, deliberately.** Python fronts the route with
// its `_EDIT_GATE` (`CATALOG_EDIT_ROLES` — every editor, readonly and the
// external trade-partner identity excluded), and a BFF-side capability check may
// only ever be *equal* to the gate it fronts: `client/docs/architecture.md`
// ("Authentication & authorization") condition 1 is that deleting the check
// changes no outcome. There is no capability in `@/lib/roles` that mirrors
// item-edit yet — `canEditCatalogLookups` names the lookup vocabulary, not
// items — and inventing one here would be ruling on access the item-edit slices
// (#249/#253) have not designed. `handleListLookups` in `./catalog-admin.ts`
// records the same reasoning for the same reason.
//
// Error scrubbing is global function middleware (`start.ts`) — no per-function
// wrapper. Both tags this call can answer with (`invalid-input:catalog-image`,
// `role-forbidden…`) are on the manifest, so their messages survive it.

interface HandlerContext {
  session: ResolvedSession;
}

async function handlePresignCatalogImageUpload({
  data,
  context,
}: {
  data: ValidatedCatalogImagePresignInput;
  context: HandlerContext;
}) {
  const { presignInternalCatalogImageUpload } =
    await import("@/server/data/internal-api/catalog-images");

  const presigned = await presignInternalCatalogImageUpload(
    { fileName: data.filename, contentType: data.contentType, sizeBytes: data.sizeBytes },
    // The session id is what Python resolves the Actor from; without it the
    // route's role gate has no roles to check and answers 401.
    { sessionId: context.session.sessionId },
  );

  // Reshaped to `@/lib/upload-to-storage`'s `PresignedUpload` — `url` is the
  // name that module's PUT reads — with the row's identity carried alongside so
  // the caller can attach the image at item save without a refetch. The `key`
  // comes from Python and is never derived here (ADR-0003: one owner of the
  // object layout).
  //
  // `contentType` is Python's normalised spelling, passed straight through: it is
  // what was signed into the URL, so it is what the PUT must send. The uploader
  // reads it off `PresignedUpload` rather than re-deriving `File.type`.
  return {
    url: presigned.uploadUrl,
    key: presigned.key,
    contentType: presigned.contentType,
    imageId: presigned.imageId,
    imageUuid: presigned.imageUuid,
  };
}

export const presignCatalogImageUpload = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(catalogImagePresignInputSchema)
  .handler(handlePresignCatalogImageUpload);

/** A presigned catalog-image upload, plus the row it was minted for. */
export interface CatalogImagePresigned extends PresignedUpload {
  contentType: string;
  imageId: number;
  imageUuid: string;
}

/**
 * The presign step in the shape `uploadToStorage` calls it — pass *this* to the
 * helper, not the server function itself.
 *
 * A `createServerFn` value takes `{ data }`, while a `PresignUploadFn` is called
 * with the input directly, so handing the raw server function to `uploadToStorage`
 * would send `{ filename, contentType }` where the validator expects them nested
 * and fail at run time. The annotation is the point: `tsc` now proves this module
 * exports something the helper can actually call, which a comment claiming the
 * same thing did not (found in cross-review, PR #324).
 */
export const presignCatalogImageForUpload: PresignUploadFn<CatalogImagePresigned> = (input) =>
  presignCatalogImageUpload({ data: input });

/**
 * One picked file, in the bucket, as a catalog image row that belongs to no item
 * yet — the presign above composed with the PUT that follows it.
 *
 * **Here rather than at each host.** Both uploader hosts — the Request Item
 * dialog and the item edit page — need exactly this composition, and it is this
 * module's own export composed with the storage helper, so a second spelling of
 * it at a call site would be a second place for the pairing to drift.
 *
 * It stays *out* of `useItemImagesUploader`, which takes the upload as a
 * parameter: that is what lets the hook's unit test run without dragging in the
 * server-function graph.
 */
export const uploadCatalogImage = (file: File, onProgress?: UploadProgressFn) =>
  uploadToStorage(file, presignCatalogImageForUpload, onProgress);

/**
 * The handler, exported for its unit tests.
 *
 * A `createServerFn` value is not callable as a plain function — invoking it
 * goes through Start's RPC plumbing — so the reshaping below would otherwise
 * only be reachable from an end-to-end test. Nothing on the request path
 * imports this object.
 */
export const __test_handlers = { handlePresignCatalogImageUpload };
